import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import SearchableDropdown from './SearchableDropdown';
import DateTimePickerInput from './DateTimePickerInput';
import { useDateFormatter } from '../hooks/useDateFormatter';
import { dateToUTCNoon } from '../utils/dateHelpers';

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  displayName?: string;
}

interface Customer {
  id: string;
  accountName: string;
  accountShortName: string;
}

interface CustomerFormData {
  accountName: string;
  accountShortName: string;
  effectiveDate: string;
  endDate: string | null;
  contactNumber: string;
  emailAddress: string;
  sbu: string;
  accountOwnerId: string;
  salesRepresentativeId: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCustomerCreated: (customer: Customer) => void;
}

const CustomerCreateModal: React.FC<Props> = ({ isOpen, onClose, onCustomerCreated }) => {
  const { getToday } = useDateFormatter();
  const [formData, setFormData] = useState<CustomerFormData>({
    accountName: '',
    accountShortName: '',
    effectiveDate: '',
    endDate: null,
    contactNumber: '',
    emailAddress: '',
    sbu: 'BU1',
    accountOwnerId: '',
    salesRepresentativeId: ''
  });

  const [users, setUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchUsers();
      // Set default effective date to today in user's timezone
      if (!formData.effectiveDate) {
        setFormData(prev => ({ ...prev, effectiveDate: getToday() }));
      }
    }
  }, [isOpen, getToday]);

  const fetchUsers = async () => {
    try {
      setUsersLoading(true);
      const response = await api.get('/customers/users/list');
      setUsers(response.data || []);
    } catch (err: any) {
      console.error('Failed to fetch users:', err);
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const payload = {
        ...formData,
        effectiveDate: dateToUTCNoon(formData.effectiveDate),
        endDate: formData.endDate ? dateToUTCNoon(formData.endDate) : null
      };

      const response = await api.post('/customers', payload);
      const newCustomer: Customer = {
        id: response.data.id,
        accountName: response.data.accountName,
        accountShortName: response.data.accountShortName
      };
      
      onCustomerCreated(newCustomer);
      handleClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create customer');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value === '' && name === 'endDate' ? null : value
    }));
  };

  const handleClose = () => {
    setFormData({
      accountName: '',
      accountShortName: '',
      effectiveDate: getToday(),
      endDate: null,
      contactNumber: '',
      emailAddress: '',
      sbu: 'BU1',
      accountOwnerId: '',
      salesRepresentativeId: ''
    });
    setError(null);
    setSubmitting(false);
    onClose();
  };

  const getUserOptions = () => {
    return users.map(user => ({
      value: user.id,
      label: user.displayName || `${user.firstName} ${user.lastName}`,
      subtitle: user.email
    }));
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    onClick={handleClose}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:ml-0 sm:mt-0 sm:text-left w-full">
                    <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900">
                      Create New Customer
                    </Dialog.Title>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Add a new customer to the system.
                      </p>
                    </div>

                    <form onSubmit={handleSubmit} className="mt-6">
                      {error && (
                        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                          {error}
                        </div>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Customer Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="accountName"
                            value={formData.accountName}
                            onChange={handleChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            placeholder="Full legal name of the account"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Short Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="accountShortName"
                            value={formData.accountShortName}
                            onChange={handleChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            placeholder="Abbreviated or display name"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Email Address <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="email"
                            name="emailAddress"
                            value={formData.emailAddress}
                            onChange={handleChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            placeholder="Primary contact email"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Contact Number <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="tel"
                            name="contactNumber"
                            value={formData.contactNumber}
                            onChange={handleChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            placeholder="Valid phone number"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Strategic Business Unit
                          </label>
                          <select
                            name="sbu"
                            value={formData.sbu}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          >
                            <option value="BU1">BU1</option>
                            <option value="BU2">BU2</option>
                            <option value="BU3">BU3</option>
                            <option value="BU4">BU4</option>
                            <option value="BU5">BU5</option>
                            <option value="BU6">BU6</option>
                            <option value="BU7">BU7</option>
                            <option value="BU8">BU8</option>
                            <option value="BU9">BU9</option>
                            <option value="BU10">BU10</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Effective Date <span className="text-red-500">*</span>
                          </label>
                          <DateTimePickerInput
                            name="effectiveDate"
                            value={formData.effectiveDate}
                            onChange={(value) => setFormData({ ...formData, effectiveDate: value })}
                            showTimeSelect={false}
                            required
                          />
                        </div>

                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            End Date
                          </label>
                          <DateTimePickerInput
                            name="endDate"
                            value={formData.endDate || ''}
                            onChange={(value) => setFormData({ ...formData, endDate: value || null })}
                            showTimeSelect={false}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Account Owner <span className="text-red-500">*</span>
                          </label>
                          {usersLoading ? (
                            <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500 sm:text-sm">
                              Loading users...
                            </div>
                          ) : (
                            <SearchableDropdown
                              value={formData.accountOwnerId || null}
                              onChange={(value) => setFormData(prev => ({ ...prev, accountOwnerId: value || '' }))}
                              options={getUserOptions()}
                              placeholder="Select account owner"
                              required
                            />
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Sales Representative <span className="text-red-500">*</span>
                          </label>
                          {usersLoading ? (
                            <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500 sm:text-sm">
                              Loading users...
                            </div>
                          ) : (
                            <SearchableDropdown
                              value={formData.salesRepresentativeId || null}
                              onChange={(value) => setFormData(prev => ({ ...prev, salesRepresentativeId: value || '' }))}
                              options={getUserOptions()}
                              placeholder="Select sales representative"
                              required
                            />
                          )}
                        </div>
                      </div>

                      <div className="mt-6 flex justify-end gap-3">
                        <button
                          type="button"
                          onClick={handleClose}
                          className="inline-flex justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={submitting || usersLoading}
                          className="inline-flex justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {submitting ? 'Creating...' : 'Create Customer'}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default CustomerCreateModal;